import type { UppKonvaAttention } from '../../model/upp-konva-types';
import type { UppFloorPlanElement } from '../plan/floor-plan-element';
import {
  UppFloorPlanArchitecture,
  UppFloorPlanBar,
  UppFloorPlanFurniture,
  UppFloorPlanVertex,
  UppFloorPlanTable,
  type UppFloorPlanBarShape,
  type UppFloorPlanTableShape,
} from '../plan/floor-plan-element';
import type { UppKonvaElementKind } from '../internal/upp-konva-scene-ref';

/** English comment normalized. */
export interface UppKonvaShapeCallbackDetail {
  readonly custom: any;
  /**
   * English comment normalized.
   * English comment normalized.
   */
  readonly stool?: number | null;
}

export type UppKonvaShapeCallback = (detail: UppKonvaShapeCallbackDetail) => void;

/**
 * English comment normalized.
 * English comment normalized.
 */
export type UppKonvaShapeData =
  | {
      type: 'table';
      chairs: number;
      /** Occupied diners (chair fill); optional. */
      diners?: number;
      /** English comment normalized. */
      shape: UppFloorPlanTableShape;
    }
  | {
      type: 'bar';
      shape: UppFloorPlanBarShape;
      stoolLeft?: number;
      stoolFront?: number;
      stoolRight?: number;
    }
  | {
      type: 'furniture';
      furnitureClass: UppFloorPlanFurniture['furnitureClass'];
      /**
       * Variante de dibujo / subtipo: en fila JSON **`furnitureShape`** (legado: **`shape`** en **`SOFA`** / **`GENERIC`**).
       * En modelo: **`genericShape`**, **`sofaShape`**, o la clase (**`billiard`**, **`football`**) cuando no hay subtipo de forma.
       */
      furnitureShape: string;
    }
  | {
      type: 'architecture';
      architectureClass: UppFloorPlanArchitecture['architectureClass'];
      /** English comment normalized. */
      architectureShape: string;
    }
  | {
      type: 'vertex';
      sort: number;
      /**
       * English comment normalized.
       */
      polygon: string;
      closed: boolean;
    };

function shapeStringForElement(el: UppFloorPlanElement): string | undefined {
  if (el instanceof UppFloorPlanVertex) {
    return undefined;
  }
  if (el instanceof UppFloorPlanTable) {
    return el.tableShape;
  }
  if (el instanceof UppFloorPlanBar) {
    return el.barShape;
  }
  if (el instanceof UppFloorPlanFurniture) {
    if (el.furnitureClass === 'generic') {
      return el.genericShape;
    }
    if (el.furnitureClass === 'sofa') {
      return el.sofaShape;
    }
    return el.furnitureClass;
  }
  if (el instanceof UppFloorPlanArchitecture) {
    return el.architectureShape;
  }
  return undefined;
}

function furnitureShapeForData(el: UppFloorPlanFurniture): string {
  if (el.furnitureClass === 'generic') {
    return el.genericShape;
  }
  if (el.furnitureClass === 'sofa') {
    return el.sofaShape;
  }
  return el.furnitureClass;
}

function dataForElement(el: UppFloorPlanElement): UppKonvaShapeData {
  if (el instanceof UppFloorPlanTable) {
    return { type: 'table', chairs: el.chairCount, shape: el.tableShape, diners: el.diners };
  }
  if (el instanceof UppFloorPlanBar) {
    return {
      type: 'bar',
      shape: el.barShape,
      stoolLeft: el.stoolLeft,
      stoolFront: el.stoolFront,
      stoolRight: el.stoolRight,
    };
  }
  if (el instanceof UppFloorPlanFurniture) {
    return {
      type: 'furniture',
      furnitureClass: el.furnitureClass,
      furnitureShape: furnitureShapeForData(el),
    };
  }
  if (el instanceof UppFloorPlanArchitecture) {
    return {
      type: 'architecture',
      architectureClass: el.architectureClass,
      architectureShape: el.architectureShape,
    };
  }
  if (el instanceof UppFloorPlanVertex) {
    return {
      type: 'vertex',
      sort: el.sort,
      polygon: el.polygon,
      closed: el.closed,
    };
  }
  throw new Error('upp-konva: unsupported element class for UppKonvaShapeHandle.data');
}

/**
 * English comment normalized.
 * English comment normalized.
 * El setter de {@link custom} solo actualiza el payload en memoria e invoca {@link onUpdated} con **`{ custom }`** — **no** repinta el lienzo ni notifica `mutation` a nivel de modelo.
 * English comment normalized.
 */
export class UppKonvaShapeHandle {
  private _onSelected?: UppKonvaShapeCallback;
  private _onUpdated?: UppKonvaShapeCallback;

  private _emitUpdated(): void {
    this._onUpdated?.({ custom: this.custom });
  }

  constructor(
    private readonly _element: UppFloorPlanElement,
    private readonly _afterMutate: () => void,
  ) {}

  /** UUID v4 de la fila (solo lectura; coincide con el snapshot **`model.area.shapes`**). */
  get uuid(): string {
    return this._element.uuid;
  }

  /** English comment normalized. */
  get type(): UppKonvaElementKind {
    return this._element.kind;
  }

  /**
   * Clave de forma para pintado cuando aplica al tipo (p. ej. `RECT` en mesas, `LINEAR` en barras).
   * English comment normalized.
   */
  get shape(): string | undefined {
    return shapeStringForElement(this._element);
  }

  get label(): string {
    return this._element.label;
  }

  set label(v: string) {
    this._element.label = v;
    this._afterMutate();
    this._emitUpdated();
  }

  get sublabel(): string {
    return this._element.sublabel;
  }

  set sublabel(v: string) {
    this._element.sublabel = v;
    this._afterMutate();
    this._emitUpdated();
  }

  /** Payload del integrador; por defecto **`null`**. No repinta: solo {@link onUpdated} con **`{ custom }`**. */
  get custom(): any {
    return this._element.custom;
  }

  set custom(v: any) {
    this._element.custom = v;
    this._emitUpdated();
  }

  /** English comment normalized. */
  get data(): UppKonvaShapeData {
    return dataForElement(this._element);
  }

  /** English comment normalized. */
  get x(): number {
    return this._element.xCm;
  }

  set x(v: number) {
    this._element.xCm = v;
    this._afterMutate();
    this._emitUpdated();
  }

  /** English comment normalized. */
  get y(): number {
    return this._element.yCm;
  }

  set y(v: number) {
    this._element.yCm = v;
    this._afterMutate();
    this._emitUpdated();
  }

  /** English comment normalized. */
  get width(): number {
    return this._element.widthCm;
  }

  set width(v: number) {
    this._element.widthCm = v;
    this._afterMutate();
    this._emitUpdated();
  }

  /** English comment normalized. */
  get height(): number {
    return this._element.heightCm;
  }

  set height(v: number) {
    this._element.heightCm = v;
    this._afterMutate();
    this._emitUpdated();
  }

  /** English comment normalized. */
  get rotation(): number {
    return this._element.rotationDeg;
  }

  set rotation(v: number) {
    this._element.rotationDeg = v;
    this._afterMutate();
    this._emitUpdated();
  }

  /**
   * English comment normalized.
   */
  get zIndex(): number {
    return this._element.zIndex;
  }

  set zIndex(v: number) {
    this._element.zIndex = Number.isFinite(v) ? Math.trunc(v) : 0;
    this._afterMutate();
    this._emitUpdated();
  }

  get onSelected(): UppKonvaShapeCallback | undefined {
    return this._onSelected;
  }

  set onSelected(fn: UppKonvaShapeCallback | undefined) {
    this._onSelected = fn;
  }

  get onUpdated(): UppKonvaShapeCallback | undefined {
    return this._onUpdated;
  }

  set onUpdated(fn: UppKonvaShapeCallback | undefined) {
    this._onUpdated = fn;
  }

  /**
   * English comment normalized.
   * @see {@link setHighlight}, {@link clearHighlight}, {@link alert}
   */
  get attention(): boolean {
    if (!(this._element instanceof UppFloorPlanTable)) {
      return false;
    }
    return this._element.attention;
  }

  /** Tono del resaltado cuando {@link attention} es true en una mesa; fuera de mesa siempre **WARNING**. */
  get attentionTone(): UppKonvaAttention {
    return this._element instanceof UppFloorPlanTable ? this._element.attentionTone : 'WARNING';
  }

  /** English comment normalized. */
  get attentionAnimated(): boolean {
    return this._element instanceof UppFloorPlanTable ? this._element.attentionAnimated : true;
  }

  /**
   * Activa el resaltado solo en **mesas**; no hace nada en otros tipos.
   * @param tone — **SUCCESS** / **WARNING** / **DANGER** (paleta del tema).
   * English comment normalized.
   */
  setHighlight(tone: UppKonvaAttention, animated: boolean): void {
    if (!(this._element instanceof UppFloorPlanTable)) {
      return;
    }
    this._element.attention = true;
    this._element.attentionTone = tone;
    this._element.attentionAnimated = animated;
    this._afterMutate();
    this._emitUpdated();
  }

  /** Quita {@link attention} y restaura tono/pulso por defecto; en cualquier tipo de fila. */
  clearHighlight(): void {
    const had = this._element.attention;
    this._element.attention = false;
    if (this._element instanceof UppFloorPlanTable) {
      this._element.attentionTone = 'WARNING';
      this._element.attentionAnimated = true;
    }
    if (had) {
      this._afterMutate();
      this._emitUpdated();
    }
  }

  /**
   * Compatibilidad: `true` equivale a {@link setHighlight}(`'WARNING'`, `true`) en mesas; `false` a {@link clearHighlight}.
   */
  alert(enabled: boolean): void {
    if (enabled) {
      this.setHighlight('WARNING', true);
    } else {
      this.clearHighlight();
    }
  }
}

/** @internal */
export function createUppKonvaShapeHandleForElement(
  el: UppFloorPlanElement,
  afterMutate: () => void,
): UppKonvaShapeHandle {
  return new UppKonvaShapeHandle(el, afterMutate);
}
